import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { adminRequest, AdminApiError } from "@/api/client";
import { useAuth } from "@/auth/useAuth";
import { isAdminToken, parseClaims } from "@/auth/claims";
import { auth } from "@/config/firebase";
import { env } from "@/config/env";
import { useFirestoreDoc } from "@/firestore/hooks";
import { COLLECTIONS } from "@/firestore/collections";
import type { UserDoc } from "@/firestore/types";

export interface UserNameChange {
  id: string;
  requestId: string;
  firstName: string;
  lastName: string;
  reason: string;
  requestedBy: string;
  verification: string | null;
  state: "pending" | "succeeded" | "failed";
  failureReason: string | null;
  updatedAt: string;
}

export function UserNameEditor({ user }: { user: UserDoc }) {
  const { user: operator, claims, capabilities } = useAuth();
  const {
    data: live,
    loading,
    error: readError,
  } = useFirestoreDoc<UserNameChange>(COLLECTIONS.userNameChanges, user.id);
  const [receipt, setReceipt] = useState<UserNameChange | null>(null);
  const current = live && (!receipt || live.updatedAt >= receipt.updatedAt) ? live : receipt;
  const [opened, setOpened] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [version, setVersion] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const request = useRef<{ input: string; id: string } | null>(null);
  const canAct =
    !!operator && !!claims?.backendId && capabilities.act && (!env.isProd || capabilities.actProd);
  const pending = current?.state === "pending";
  const stale = opened && version !== user.updatedAt;
  const valid =
    firstName.trim().length > 0 && lastName.trim().length > 0 && reason.trim().length > 0;

  function open() {
    setFirstName(current?.state === "failed" ? current.firstName : user.firstName);
    setLastName(current?.state === "failed" ? current.lastName : user.lastName);
    setReason("");
    setVersion(user.updatedAt);
    setConfirmed(false);
    setError(null);
    request.current = null;
    setOpened(true);
  }

  async function save() {
    if (!canAct || !valid || !confirmed || stale || pending || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError(null);
    const uid = operator!.uid;
    const backendId = claims!.backendId;
    try {
      const signedIn = auth.currentUser;
      if (!signedIn || signedIn.uid !== uid)
        throw new Error("Your sign-in changed. Reopen the name editor.");
      const token = await signedIn.getIdTokenResult(true);
      const freshClaims = parseClaims(token);
      if (
        auth.currentUser?.uid !== uid ||
        freshClaims.backendId !== backendId ||
        !isAdminToken(freshClaims)
      ) {
        throw new Error("Your operator access changed. Reopen the name editor.");
      }
      const input = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        reason: reason.trim(),
      };
      const serialized = JSON.stringify(input);
      if (request.current?.input !== serialized)
        request.current = { input: serialized, id: crypto.randomUUID() };
      const result = await adminRequest<UserNameChange>({
        method: "POST",
        path: `/users/${encodeURIComponent(user.id)}/nameChanges`,
        query: { updatedAt: version },
        body: { ...input, requestId: request.current.id },
        token: token.token,
      });
      setReceipt(result);
      setOpened(false);
    } catch (e) {
      setError(
        e instanceof AdminApiError && e.conflict
          ? "This profile changed while you were editing. Close and reopen the editor to review the latest values."
          : e instanceof Error
            ? e.message
            : "The correction could not be submitted. Retry to check the same request.",
      );
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  return (
    <Card withBorder radius="md" padding="lg">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Name correction</Text>
          <Button
            size="xs"
            variant="default"
            disabled={!canAct || pending || saving || loading || !!readError}
            onClick={open}
          >
            {current?.state === "failed" ? "Review and retry" : "Edit name"}
          </Button>
        </Group>
        <Text size="sm" c="dimmed">
          Save once to update the Elios profile and linked Rain user. Existing card names in Elios
          refresh automatically.
        </Text>
        {readError && (
          <Alert color="red">Unable to read name correction status: {readError.message}</Alert>
        )}
        {current && (
          <Alert
            color={
              current.state === "failed" ? "red" : current.state === "pending" ? "blue" : "green"
            }
            title={
              current.state === "pending"
                ? "Updating name"
                : current.state === "failed"
                  ? "Name correction needs attention"
                  : current.verification
                    ? "Updated in Elios and Rain"
                    : "Updated in Elios"
            }
          >
            <Text size="sm">
              {current.firstName} {current.lastName}
            </Text>
            <Text size="sm">
              {current.state === "pending"
                ? "Waiting for confirmation. Temporary failures are retried automatically; you can leave this page."
                : current.state === "failed"
                  ? current.failureReason
                  : current.verification
                    ? "Both updates are confirmed. Elios card displays may take a few seconds to refresh."
                    : "No linked KYC user existed, so no Rain update was needed."}
            </Text>
            <Text size="xs" mt="xs">
              Reason: {current.reason}
            </Text>
          </Alert>
        )}
      </Stack>
      <Modal
        opened={opened}
        onClose={() => {
          if (!saving) setOpened(false);
        }}
        title="Correct user name"
        closeOnClickOutside={!saving}
        closeOnEscape={!saving}
        withCloseButton={!saving}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <Stack>
            <Text size="sm">
              Current name: {user.firstName} {user.lastName}
            </Text>
            <TextInput
              label="First name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.currentTarget.value);
                setConfirmed(false);
              }}
              required
              maxLength={100}
              disabled={saving}
            />
            <TextInput
              label="Last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.currentTarget.value);
                setConfirmed(false);
              }}
              required
              maxLength={100}
              disabled={saving}
            />
            <Textarea
              label="Reason for correction"
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              required
              maxLength={500}
              disabled={saving}
            />
            <Text size="sm">
              This updates the profile, linked Rain user and names shown on cards in Elios. Names on
              issued physical cards or in Apple/Google Wallet may require Rain support.
            </Text>
            <Checkbox
              label="I have verified these names with the user."
              checked={confirmed}
              onChange={(e) => setConfirmed(e.currentTarget.checked)}
              disabled={saving}
            />
            {stale && (
              <Alert color="yellow">
                The profile changed. Close and reopen this editor before saving.
              </Alert>
            )}
            {error && <Alert color="red">{error}</Alert>}
            <Group justify="flex-end">
              <Button variant="default" disabled={saving} onClick={() => setOpened(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={saving}
                disabled={!canAct || !valid || !confirmed || stale || pending}
              >
                Save name
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
